import { tenantLoginPath } from './tenantLoginUrl.js';

const PATHS_KEY = 'alinea_user_last_paths';
const LOGIN_SLUGS_KEY = 'alinea_user_login_slugs';
const LAST_TENANT_SLUG_KEY = 'alinea_last_tenant_slug';
const RETURN_TO_KEY = 'alinea_return_to';

const SKIP_PATH_PREFIXES = ['/login', '/kiosko', '/terms', '/privacy'];

function readMap(key) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function writeMap(key, map) {
    localStorage.setItem(key, JSON.stringify(map));
}

function isNavigablePath(path) {
    if (!path || typeof path !== 'string') return false;
    return !SKIP_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** Clave estable por usuario dentro de su empresa. */
export function userNavigationKey(user) {
    if (!user) return null;
    const id = user.id ?? user.employeeId ?? user.employee_id;
    const tid = user.tenantId ?? user.tenant_id;
    if (id != null && tid != null) return `${tid}:${id}`;
    const code = user.codigo_empleado ?? user.employeeCode ?? user.employee_code;
    return code ? String(code).trim().toUpperCase() : null;
}

export function saveUserLastPath(user, path) {
    const key = userNavigationKey(user);
    if (!key || !isNavigablePath(path)) return;
    const map = readMap(PATHS_KEY);
    map[key] = path;
    writeMap(PATHS_KEY, map);
}

export function getUserLastPath(user) {
    const key = userNavigationKey(user);
    if (!key) return null;
    const path = readMap(PATHS_KEY)[key];
    return isNavigablePath(path) ? path : null;
}

export function saveUserLoginSlug(user, slug) {
    const key = userNavigationKey(user);
    if (!key) return;
    const normalized = String(slug || '')
        .trim()
        .toLowerCase();
    const map = readMap(LOGIN_SLUGS_KEY);
    map[key] = normalized || null;
    writeMap(LOGIN_SLUGS_KEY, map);
}

export function getUserLoginSlug(user) {
    const key = userNavigationKey(user);
    if (!key) return null;
    return readMap(LOGIN_SLUGS_KEY)[key] || null;
}

export function persistLastTenantSlug(slug) {
    const normalized = String(slug || '')
        .trim()
        .toLowerCase();
    if (normalized) localStorage.setItem(LAST_TENANT_SLUG_KEY, normalized);
}

export function clearLastTenantSlug() {
    localStorage.removeItem(LAST_TENANT_SLUG_KEY);
}

export function getLastTenantSlug() {
    return localStorage.getItem(LAST_TENANT_SLUG_KEY) || '';
}

export function rememberReturnPath(path) {
    if (!isNavigablePath(path)) return;
    sessionStorage.setItem(RETURN_TO_KEY, path);
}

export function resolveLoginPath({ user, fallbackSlug } = {}) {
    if (user?.rol === 'superadmin') {
        const remembered = getUserLoginSlug(user);
        return remembered ? tenantLoginPath(remembered) : '/login';
    }
    const slug =
        user?.tenantSlug ||
        user?.tenant_slug ||
        getUserLoginSlug(user) ||
        fallbackSlug ||
        getLastTenantSlug();
    return tenantLoginPath(slug);
}

export function resolvePostLoginPath(user) {
    const pending = sessionStorage.getItem(RETURN_TO_KEY);
    if (pending && isNavigablePath(pending)) {
        sessionStorage.removeItem(RETURN_TO_KEY);
        saveUserLastPath(user, pending);
        return pending;
    }
    const last = getUserLastPath(user);
    if (last) return last;
    return '/';
}

export function redirectToLogin({ user, fallbackSlug, returnPath } = {}) {
    if (typeof window === 'undefined') return;
    if (returnPath) rememberReturnPath(returnPath);
    else if (isNavigablePath(window.location.pathname + window.location.search)) {
        rememberReturnPath(window.location.pathname + window.location.search);
    }
    window.location.href = resolveLoginPath({ user, fallbackSlug });
}
